import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { api } from "./api";

export interface MetaTreeItem {
  id: string;
  name: string;
  status: string;
}

export type MetaTreeError = "no_ad_account" | "not_configured" | "meta_error";

export type MetaTreeResponse =
  | { ok: true; items: MetaTreeItem[]; truncated?: boolean }
  | { ok: false; error: MetaTreeError; message?: string };

/**
 * Campaign/ad-set/ad picker for the touchpoint-insert form (TASK-60, S3).
 * Active entries only by default; `all` widens the fetch to every status
 * (paused/completed included) — the Combobox's own type-to-filter then
 * searches within whatever list came back (no server-side name search, see
 * apps/api/src/lib/meta-campaign-tree.ts). No client cache beyond React
 * Query's normal in-session dedup — the backend itself never caches Meta's
 * answer either (PRD decision).
 */
export function useMetaCampaigns(projectId: number, all: boolean) {
  return useQuery<MetaTreeResponse>({
    queryKey: ["admin", "meta-campaign-tree", "campaigns", projectId, all],
    queryFn: () => api(`/admin/projects/${projectId}/meta-campaigns${all ? "?all=1" : ""}`),
    enabled: projectId > 0,
    // Keep showing the active-only list while the "all" search refetch is in
    // flight — otherwise the queryKey change would flip isLoading back to
    // true and the caller would unmount the picker mid-search (losing the
    // operator's typed text in the Combobox).
    placeholderData: keepPreviousData,
  });
}

/** Ad sets of ONE campaign — never the whole account (dependent level, S3). */
export function useMetaAdSets(campaignId: string | null, all: boolean) {
  return useQuery<MetaTreeResponse>({
    queryKey: ["admin", "meta-campaign-tree", "adsets", campaignId, all],
    queryFn: () =>
      api(`/admin/meta/campaigns/${encodeURIComponent(campaignId as string)}/adsets${all ? "?all=1" : ""}`),
    enabled: campaignId !== null,
    placeholderData: keepPreviousData,
  });
}

/** Ads of ONE ad set — never the whole account (dependent level, S3). */
export function useMetaAds(adSetId: string | null, all: boolean) {
  return useQuery<MetaTreeResponse>({
    queryKey: ["admin", "meta-campaign-tree", "ads", adSetId, all],
    queryFn: () => api(`/admin/meta/adsets/${encodeURIComponent(adSetId as string)}/ads${all ? "?all=1" : ""}`),
    enabled: adSetId !== null,
    placeholderData: keepPreviousData,
  });
}
